import { createDisposer, type Maybe, throttle, type Unsubscribe } from '@amzn/vinyl-util'
import { windowEvents } from '@/util/interaction'
import { data, type ObservableValue, externalData } from '@amzn/vinyl-observable'

/**
 * The percents an element must be towards the center before scrolling should be skipped.
 */
const CENTER_THRESHOLD_X = 0.1
const CENTER_THRESHOLD_Y = 0.1

/**
 * Focuses an element, preventing scrolling (rely on the scrollIntoViewOnFocus binding)
 */
export function focus(el: Maybe<HTMLElement>) {
    if (!el) return
    el.focus({ preventScroll: true })
}

let scrollBehavior: ScrollBehavior = 'smooth'

/**
 * Sets the scroll behavior.
 * 'smooth', 'instant', or 'auto'
 */
export function setScrollBehavior(value: ScrollBehavior) {
    scrollBehavior = value
}

export function scrollIntoViewIfNeeded(el: HTMLElement) {
    const rect = el.getBoundingClientRect()
    const vW = window.innerWidth
    const vH = window.innerHeight
    const marginW = CENTER_THRESHOLD_X * vW
    const marginH = CENTER_THRESHOLD_Y * vH
    const scrollX = rect.left < marginW || rect.right > vW - marginW
    const scrollY = rect.top < marginH || rect.bottom > vH - marginH

    if (scrollX || scrollY) {
        el.scrollIntoView({
            block: scrollY ? 'center' : 'nearest',
            inline: scrollX ? 'center' : 'nearest',
            behavior: scrollBehavior,
        })
    }
}

export function scrollIntoViewOnFocus(): Unsubscribe {
    const { add, dispose } = createDisposer()

    let lastInteraction: 'keyboard' | 'pointer' | null = null

    add(
        windowEvents.on(
            'keydown',
            () => {
                lastInteraction = 'keyboard'
            },
            { capture: true }
        )
    )

    add(
        windowEvents.on(
            'mousedown',
            () => {
                lastInteraction = 'pointer'
            },
            { capture: true }
        )
    )

    add(
        windowEvents.on(
            'pointerdown',
            () => {
                lastInteraction = 'pointer'
            },
            { capture: true }
        )
    )

    let scrollTarget: HTMLElement | null = null
    const throttledScrollInfoView = throttle(
        () => {
            if (scrollTarget) {
                scrollIntoViewIfNeeded(scrollTarget)
                scrollTarget = null
            }
        },
        0.3,
        { leading: true, trailing: true }
    )
    add(
        windowEvents.on('focusin', (event) => {
            if (event.target && lastInteraction === 'keyboard') {
                scrollTarget = event.target as HTMLElement
                throttledScrollInfoView()
            }
        })
    )
    return dispose
}

/**
 * Scrolls to top, instantly.
 */
export function scrollToTop() {
    document.scrollingElement?.scroll({
        top: 0,
        left: 0,
        behavior: 'instant',
    })
}

export function isIntersectingProvider(
    element: HTMLElement,
    init: IntersectionObserverInit
): ObservableValue<boolean> {
    const isIntersecting = data(false)
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            isIntersecting.value = entry.isIntersecting
        })
    }, init)
    let observers = 0

    return externalData(isIntersecting.value, (setData) => {
        if (!observers++) observer.observe(element)
        const sub = isIntersecting.onData((newValue) => {
            setData(newValue)
        })
        return () => {
            sub()
            if (!--observers) observer.unobserve(element)
        }
    })
}

/**
 * Given an element, returns the nearest parent with scroll overflow behavior.
 */
export function getScrollParent(
    el: HTMLElement,
    direction: 'vertical' | 'horizontal' = 'vertical'
): Element | null {
    let parent = el.parentElement
    while (parent) {
        const style = getComputedStyle(parent)
        const canScroll = overflowAllowsScroll(
            direction === 'horizontal' ? style.overflowX : style.overflowY
        )
        if (canScroll) return parent
        parent = parent.parentElement
    }
    return document.scrollingElement
}

function overflowAllowsScroll(overflow: string): boolean {
    return overflow === 'auto' || overflow === 'scroll'
}

export function getViewportRect(): DOMRect {
    return {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
        width: window.innerWidth,
        height: window.innerHeight,
        x: 0,
        y: 0,
        toJSON: () => null,
    }
}

/**
 * Converts a viewport-relative DOMRect to coordinates relative to a given DOMRect base.
 *
 * @param rect - The DOMRect to convert (e.g. from getBoundingClientRect).
 * @param base - The DOMRect to convert relative to (usually from relativeTo.getBoundingClientRect()).
 * @returns A DOMRect-like object with coordinates relative to the base rect.
 */
export function toLocalRect(rect: DOMRect, base: DOMRect): DOMRect {
    return {
        x: rect.x - base.x,
        y: rect.y - base.y,
        left: rect.left - base.left,
        top: rect.top - base.top,
        right: rect.right - base.left,
        bottom: rect.bottom - base.top,
        width: rect.width,
        height: rect.height,
        toJSON: () => null,
    }
}

/**
 * Returns a promise that resolves on the next render frame.
 */
export function nextRenderFrame(): Promise<number> {
    return new Promise(requestAnimationFrame)
}

export function updateQueryParam(key: string, value: string | null) {
    const url = new URL(window.location.href)

    if (value === null) {
        url.searchParams.delete(key)
    } else {
        url.searchParams.set(key, value)
    }
    window.location.href = url.toString()
}

/**
 * Creates a ResizeObserver, watching the specified element.
 * When the returned Unsubscribe is called, the observer will disconnect.
 *
 * @param element
 * @param callback
 */
export function onResize(element: HTMLElement, callback: () => void): Unsubscribe {
    const resizeObserver = new ResizeObserver(callback)
    resizeObserver.observe(element)
    callback()
    return () => {
        resizeObserver.disconnect()
    }
}
